import { useEffect, useRef, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { User, Upload, Trash2, ImageIcon, Save, ExternalLink } from 'lucide-react';
import Header from '../components/Header';

export default function AdminMeetGenesis() {
  const [data, setData] = useState(null);
  const [story, setStory] = useState('');
  const [funHeading, setFunHeading] = useState('');
  const [funIntro, setFunIntro] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [caption, setCaption] = useState('');
  const heroRef = useRef();
  const galleryRef = useRef();

  const load = async () => {
    const { data } = await api.get('/about');
    setData(data);
    setStory(data.story || '');
    setFunHeading(data.fun_heading || '');
    setFunIntro(data.fun_intro || '');
  };
  useEffect(() => { load(); }, []);

  const saveText = async () => {
    setSaving(true);
    try {
      await api.put('/admin/about', { story, fun_heading: funHeading, fun_intro: funIntro });
      toast.success('Saved');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error('Max 20 MB — try a smaller photo'); return; }
    setUploadingHero(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post('/admin/about/hero-photo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Hero photo updated');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploadingHero(false); }
  };

  const uploadGallery = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingGallery(true);
    let ok = 0;
    for (const file of files) {
      if (file.size > 20 * 1024 * 1024) { toast.error(`${file.name} is too large (max 20 MB)`); continue; }
      const form = new FormData();
      form.append('file', file);
      try {
        await api.post(`/admin/about/gallery?caption=${encodeURIComponent(caption)}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok++;
      } catch (err) {
        toast.error(`Upload failed for ${file.name}`);
      }
    }
    if (ok) toast.success(`Uploaded ${ok} photo${ok > 1 ? 's' : ''}`);
    setCaption('');
    await load();
    setUploadingGallery(false);
  };

  const deletePhoto = async (id) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await api.delete(`/admin/about/gallery/${id}`);
      toast.success('Photo removed');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const updateCaption = async (id, newCaption) => {
    try {
      await api.patch(`/admin/about/gallery/${id}`, { caption: newCaption });
      toast.success('Caption saved');
    } catch (err) {
      toast.error('Save failed');
    }
  };

  const inputStyle = {
    fontFamily: 'Manrope, sans-serif',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E6E2D8',
    borderRadius: '12px',
    padding: '12px 14px',
    color: '#2C2724',
    width: '100%',
    fontSize: '15px',
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-xl" style={{ backgroundColor: '#E57A4415' }}>
            <User className="w-5 h-5" style={{ color: '#E57A44' }} />
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
            Edit "Meet Genesis"
          </h1>
          <a
            href="/meet-genesis"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-sm"
            style={{ fontFamily: 'Manrope, sans-serif', color: '#5B7C99', fontWeight: 600 }}
            data-testid="preview-meet-genesis-link"
          >
            View page <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <p className="mb-8" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>
          Edit your story, mission, and photos here. Changes save immediately and appear on the public "Meet Genesis" page.
        </p>

        {/* Hero photo */}
        <section className="mb-10">
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Hero photo</h2>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div
              className="rounded-2xl overflow-hidden flex items-center justify-center flex-shrink-0"
              style={{ width: 240, height: 180, backgroundColor: '#F3EFE7' }}
              data-testid="admin-hero-preview"
            >
              {data?.hero_photo_url ? (
                <img src={data.hero_photo_url} alt="Hero" style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }} />
              ) : (
                <ImageIcon className="w-8 h-8" style={{ color: '#B8AF9D' }} />
              )}
            </div>
            <div>
              <input
                ref={heroRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadHero(e.target.files?.[0])}
                data-testid="admin-hero-input"
              />
              <button
                onClick={() => heroRef.current?.click()}
                disabled={uploadingHero}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full transition"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600, opacity: uploadingHero ? 0.6 : 1 }}
                data-testid="admin-hero-upload-btn"
              >
                <Upload className="w-4 h-4" />
                {uploadingHero ? 'Uploading…' : (data?.hero_photo_url ? 'Replace photo' : 'Upload hero photo')}
              </button>
              <p className="text-xs mt-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D' }}>JPG or PNG · max 20 MB · any orientation works (portrait, landscape, or square)</p>
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="mb-10">
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Your story & mission</h2>
          <p className="text-xs mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D' }}>Paste your bio, story, and mission. Separate paragraphs with a blank line.</p>
          <textarea
            value={story}
            onChange={(e) => setStory(e.target.value)}
            rows={12}
            style={{ ...inputStyle, fontFamily: 'Lora, serif', lineHeight: 1.7 }}
            placeholder="Hi, I'm Genesis..."
            data-testid="admin-story-textarea"
          />
        </section>

        {/* Fun section headings */}
        <section className="mb-10">
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>"Adventurous side" section</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm mb-1 block" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D', fontWeight: 500 }}>Heading</label>
              <input
                value={funHeading}
                onChange={(e) => setFunHeading(e.target.value)}
                style={inputStyle}
                placeholder="A few things I love"
                data-testid="admin-fun-heading"
              />
            </div>
            <div>
              <label className="text-sm mb-1 block" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D', fontWeight: 500 }}>Intro line</label>
              <input
                value={funIntro}
                onChange={(e) => setFunIntro(e.target.value)}
                style={inputStyle}
                placeholder="When I'm not writing or praying, here's a glimpse of my adventurous side."
                data-testid="admin-fun-intro"
              />
            </div>
          </div>
        </section>

        <button
          onClick={saveText}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition mb-12"
          style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#5C715E', color: '#FDFBF7', fontWeight: 700, opacity: saving ? 0.6 : 1 }}
          data-testid="admin-save-story-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save story & headings'}
        </button>

        {/* Gallery */}
        <section className="mb-12">
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Adventure photos</h2>
          <p className="text-xs mb-4" style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D' }}>Upload up to 8 photos of things you love — hiking, coffee, travel, family, hobbies. Each gets an optional caption.</p>

          <div className="p-4 rounded-2xl mb-4" style={{ backgroundColor: '#FFF7ED', border: '1px solid #FDBA7440' }}>
            <label className="text-sm mb-1 block" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D', fontWeight: 500 }}>Caption for next upload (optional)</label>
            <input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              style={inputStyle}
              placeholder="e.g. Sunrise hike in Maine, Summer 2024"
              data-testid="admin-next-caption"
            />
            <input
              ref={galleryRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => uploadGallery(Array.from(e.target.files || []))}
              data-testid="admin-gallery-input"
            />
            <button
              onClick={() => galleryRef.current?.click()}
              disabled={uploadingGallery}
              className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full transition"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600, opacity: uploadingGallery ? 0.6 : 1 }}
              data-testid="admin-gallery-upload-btn"
            >
              <Upload className="w-4 h-4" />
              {uploadingGallery ? 'Uploading…' : 'Add photo(s)'}
            </button>
          </div>

          {(data?.gallery || []).length === 0 ? (
            <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: '#FFFFFF', border: '2px dashed #E6D5B8', color: '#B8AF9D' }} data-testid="admin-gallery-empty">
              <ImageIcon className="w-8 h-8 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>No photos yet — upload your first one above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-gallery-grid">
              {data.gallery.map((p) => (
                <div key={p.id} className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} data-testid={`admin-photo-${p.id}`}>
                  <div className="aspect-square" style={{ backgroundColor: '#F3EFE7' }}>
                    <img src={p.url} alt={p.caption} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <input
                      defaultValue={p.caption}
                      onBlur={(e) => e.target.value !== p.caption && updateCaption(p.id, e.target.value)}
                      className="text-xs w-full mb-2 px-2 py-1 rounded-lg"
                      style={{ fontFamily: 'Manrope, sans-serif', border: '1px solid #E6E2D8', color: '#2C2724' }}
                      placeholder="Add caption…"
                      data-testid={`admin-photo-caption-${p.id}`}
                    />
                    <button
                      onClick={() => deletePhoto(p.id)}
                      className="text-xs inline-flex items-center gap-1"
                      style={{ fontFamily: 'Manrope, sans-serif', color: '#DC2626', fontWeight: 600 }}
                      data-testid={`admin-photo-delete-${p.id}`}
                    >
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
